// Trocar quem assina num documento que JÁ está na D4 Sign.
//
// Pedido do Lucas em 04/10/2026, na VND-2026-087: o contato do cliente mudou
// depois do envio. O único caminho que existia era cancelar o documento e
// mandar outro — e naquela venda o Gustavo já tinha assinado, então cancelar
// jogaria a assinatura dele fora.
//
// Peça ÚNICA das duas telas que assinam (expediente e venda): quem chama passa
// a função que fala com o servidor. Duas telas com dois formulários iguais
// viram duas regras diferentes na primeira correção.
import { useState } from 'react';
import {
  App as AntApp, Alert, Button, Form, Input, Modal, Select, Space, Tag, Typography,
} from 'antd';
import type { Envelope } from './tipos';

const { Text } = Typography;

export interface TrocaResposta {
  trocado: boolean;
  de: { nome: string; email: string };
  para: { nome: string; email: string };
  papel: string | null;
  avisado: boolean;
  aviso?: string | null;
}

interface Props {
  aberto: boolean;
  envelope: Envelope;
  /** Chama o servidor. Quem passa decide a action (expediente ou venda). */
  trocar: (p: { de_email: string; para_nome: string; para_email: string;
                para_documento?: string }) => Promise<TrocaResposta>;
  aoFechar: () => void;
  /** Recarrega a tela depois de uma troca bem-sucedida. */
  aoTrocar: () => void;
}

export default function ModalTrocarSignatario({
  aberto, envelope, trocar, aoFechar, aoTrocar,
}: Props) {
  const { message } = AntApp.useApp();
  const [form] = Form.useForm();
  const [salvando, setSalvando] = useState(false);

  // 🚨 Só quem NÃO assinou. Trocar quem já assinou reescreveria a identidade
  //    de quem assinou um documento com valor jurídico — o servidor recusa, e
  //    a tela não pode oferecer o que ela sabe que vai ser recusado.
  const pendentes = (envelope.signatarios || []).filter(s => s.status !== 'assinado');
  const jaAssinaram = (envelope.signatarios || []).filter(s => s.status === 'assinado');

  const enviar = async () => {
    const v = await form.validateFields();
    setSalvando(true);
    try {
      const r = await trocar({
        de_email: v.de_email,
        para_nome: String(v.para_nome).trim(),
        para_email: String(v.para_email).trim().toLowerCase(),
        para_documento: v.para_documento ? String(v.para_documento).trim() : undefined,
      });
      // O aviso de "trocou mas não consegui mandar o e-mail" é o caso que não
      // pode passar como sucesso puro: a pessoa nova ficaria esperando.
      if (r.avisado) {
        message.success(`${r.para.nome} agora assina no lugar de ${r.de.nome}, e já recebeu o link.`, 7);
      } else {
        message.warning(r.aviso || 'Troca feita, mas o link não foi enviado.', 12);
      }
      form.resetFields();
      aoTrocar();
      aoFechar();
    } catch (e: any) {
      message.error(e.response?.data?.error || e.message, 12);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Modal
      open={aberto}
      title="Trocar quem assina"
      onCancel={() => { form.resetFields(); aoFechar(); }}
      okText="Trocar e avisar"
      cancelText="Voltar"
      confirmLoading={salvando}
      onOk={enviar}
      width="94vw"
      style={{ maxWidth: 620, top: 24 }}
      styles={{ body: { maxHeight: 'calc(100vh - 220px)', overflowY: 'auto' } }}
      okButtonProps={{ disabled: !pendentes.length }}
    >
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Alert
          type="info" showIcon
          message="O documento continua o mesmo"
          description={
            'Ele não é cancelado nem reenviado do zero: só a pessoa muda, e quem entra '
            + 'recebe o link de assinatura. Quem já assinou não precisa assinar de novo.'
          } />

        {!pendentes.length ? (
          <Alert type="warning" showIcon
            message="Não há quem trocar"
            description="Todos os signatários deste documento já assinaram." />
        ) : (
          <Form form={form} layout="vertical" requiredMark={false}>
            <Form.Item
              name="de_email" label="Quem sai"
              rules={[{ required: true, message: 'Escolha quem sai' }]}
              initialValue={pendentes.length === 1 ? pendentes[0].email : undefined}>
              {/* Busca por escrita: regra da casa para todo dropdown. */}
              <Select
                showSearch optionFilterProp="label" placeholder="Escolha o signatário"
                options={pendentes.map(s => ({
                  value: s.email,
                  label: `${s.nome} · ${s.email}${s.qualificacao ? ` — ${s.qualificacao}` : ''}`,
                }))} />
            </Form.Item>

            <Form.Item name="para_nome" label="Quem entra — nome completo"
              rules={[{ required: true, min: 3, message: 'Informe o nome de quem entra' }]}>
              <Input placeholder="Nome como deve sair no documento" />
            </Form.Item>

            <Form.Item name="para_email" label="Quem entra — e-mail"
              rules={[
                { required: true, message: 'Informe o e-mail' },
                { type: 'email', message: 'E-mail inválido' },
              ]}>
              <Input placeholder="nome@empresa.com.br" />
            </Form.Item>

            <Form.Item name="para_documento" label="CPF de quem entra (opcional)">
              <Input placeholder="Só se o documento exigir" />
            </Form.Item>

            {/* O papel não muda, e dizer isso evita a expectativa errada: quem
                precisa mudar Contratante/Contratada tem de cancelar e mandar
                outro, senão o documento sairia sem uma das partes. */}
            <Text type="secondary" style={{ fontSize: 12 }}>
              Quem entra assume o <strong>mesmo papel</strong> de quem sai
              (Contratante, Contratada, Testemunha…). Para mudar o papel, é preciso
              cancelar o envio e mandar um documento novo.
            </Text>
          </Form>
        )}

        {!!jaAssinaram.length && (
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>Já assinaram, e não entram na troca: </Text>
            {jaAssinaram.map(s => (
              <Tag key={s.email} color="green" style={{ marginTop: 4 }}>{s.nome}</Tag>
            ))}
          </div>
        )}
      </Space>
    </Modal>
  );
}
